import { type BattleState, type CombatSide, type Move } from '@/game/battle';

const FLOAT_LIFETIME_MS = 1200;

export type BattleChoice = 'recruit' | 'skip';

export class BattleHud {
  private readonly el: HTMLElement;
  private readonly playerName: HTMLElement;
  private readonly playerFill: HTMLElement;
  private readonly playerNum: HTMLElement;
  private readonly enemyName: HTMLElement;
  private readonly enemyFill: HTMLElement;
  private readonly enemyNum: HTMLElement;
  private readonly action: HTMLElement;
  private readonly choices: HTMLElement;
  private readonly floats: HTMLElement;
  private onMoveChoice: ((idx: number) => void) | null = null;
  private onPostBattleChoice: ((choice: BattleChoice) => void) | null = null;

  constructor(root: HTMLElement) {
    this.el = root;
    this.playerName = mustGet(root, '.hp-bar.player .hp-bar-name');
    this.playerFill = mustGet(root, '.hp-bar.player .hp-bar-fill');
    this.playerNum = mustGet(root, '.hp-bar.player .hp-bar-num');
    this.enemyName = mustGet(root, '.hp-bar.enemy .hp-bar-name');
    this.enemyFill = mustGet(root, '.hp-bar.enemy .hp-bar-fill');
    this.enemyNum = mustGet(root, '.hp-bar.enemy .hp-bar-num');
    this.action = mustGet(root, '.battle-action');
    this.choices = mustGet(root, '.battle-choices');
    this.floats = mustGet(root, '.battle-floats');

    this.choices.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const moveIdx = target.dataset.moveIdx;
      if (moveIdx !== undefined) {
        this.onMoveChoice?.(Number(moveIdx));
        return;
      }
      const choice = target.dataset.choice;
      if (choice === 'recruit' || choice === 'skip') {
        this.onPostBattleChoice?.(choice);
      }
    });
  }

  show(state: BattleState): void {
    this.el.classList.add('visible');
    this.playerName.textContent = state.player.name;
    this.enemyName.textContent = state.enemy.name;
    this.update(state);
  }

  hide(): void {
    this.el.classList.remove('visible');
    this.choices.replaceChildren();
    this.choices.hidden = true;
    this.floats.replaceChildren();
    this.onMoveChoice = null;
    this.onPostBattleChoice = null;
    this.action.textContent = '';
  }

  update(state: BattleState): void {
    setBar(this.playerFill, this.playerNum, state.player.hp, state.player.maxHp);
    setBar(this.enemyFill, this.enemyNum, state.enemy.hp, state.enemy.maxHp);
    this.action.textContent = actionText(state);
  }

  showMoveChoices(moves: ReadonlyArray<Move>, handler: (idx: number) => void): void {
    this.onMoveChoice = handler;
    this.onPostBattleChoice = null;
    this.choices.replaceChildren();
    for (let i = 0; i < moves.length; i++) {
      const m = moves[i]!;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'battle-btn primary';
      btn.dataset.moveIdx = String(i);
      btn.textContent = m.name;
      this.choices.appendChild(btn);
    }
    this.choices.hidden = false;
    this.action.textContent = '';
  }

  showRecruitChoice(handler: (choice: BattleChoice) => void): void {
    this.onPostBattleChoice = handler;
    this.onMoveChoice = null;
    this.choices.replaceChildren();
    const recruit = document.createElement('button');
    recruit.type = 'button';
    recruit.className = 'battle-btn primary';
    recruit.dataset.choice = 'recruit';
    recruit.textContent = 'Recruit';
    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'battle-btn';
    skip.dataset.choice = 'skip';
    skip.textContent = 'Skip';
    this.choices.appendChild(recruit);
    this.choices.appendChild(skip);
    this.choices.hidden = false;
    this.action.textContent = '';
  }

  hideChoices(): void {
    this.choices.hidden = true;
    this.choices.replaceChildren();
    this.onMoveChoice = null;
    this.onPostBattleChoice = null;
  }

  spawnDamage(side: CombatSide, amount: number): void {
    this.spawnFloat(side, amount.toString(), '');
  }

  spawnMiss(side: CombatSide): void {
    this.spawnFloat(side, 'miss', 'miss');
  }

  private spawnFloat(side: CombatSide, text: string, extraClass: string): void {
    const float = document.createElement('div');
    const sideClass = side === 'player' ? 'player-side' : 'enemy-side';
    float.className = `battle-float ${sideClass}${extraClass !== '' ? ` ${extraClass}` : ''}`;
    float.textContent = text;
    this.floats.appendChild(float);
    window.setTimeout(() => float.remove(), FLOAT_LIFETIME_MS);
  }
}

const mustGet = (root: HTMLElement, selector: string): HTMLElement => {
  const el = root.querySelector<HTMLElement>(selector);
  if (el === null) throw new Error(`battle-hud: missing element ${selector}`);
  return el;
};

const setBar = (fill: HTMLElement, num: HTMLElement, hp: number, maxHp: number): void => {
  const pct = maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
  fill.style.width = `${pct}%`;
  num.textContent = `${hp}/${maxHp}`;
};

const actionText = (state: BattleState): string => {
  if (state.status === 'end-victory') return 'Victory';
  if (state.status === 'end-defeat') return 'Defeated';
  if (state.whoseTurn === 'enemy') return `${state.enemy.name}…`;
  return '';
};
